// Database schema verification test
import { getSupabaseUrl, getSupabaseAnonKey } from '../config/supabase';

export const runDatabaseSchemaTest = async () => {
  console.log('🔍 Starting database schema verification test...');
  
  const results = {
    tablesFound: [],
    tablesMissing: [],
    errors: []
  };

  try {
    const supabaseUrl = getSupabaseUrl();
    const supabaseKey = getSupabaseAnonKey();

    // List of expected tables
    const expectedTables = [
      'calendar_weeks',
      'scheduled_movies', 
      'movie_reviews',
      'starred_movies',
      'predefined_names',
      'movie_status',
      'email_users',
      'week_visibility',
      'survey_votes',
      'tmdb_cache',
      'movie_collection'
    ];

    console.log('📋 Checking for expected tables...');

    for (const tableName of expectedTables) {
      try {
        console.log(`🔍 Testing table: ${tableName}`);
        
        // Try a simple SELECT with LIMIT 0 to check if table exists
        const response = await fetch(`${supabaseUrl}/rest/v1/${tableName}?select=*&limit=0`, {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`
          },
          timeout: 5000
        });

        if (response.ok) {
          console.log(`✅ Table '${tableName}' exists`);
          results.tablesFound.push(tableName);
        } else if (response.status === 404) {
          console.log(`❌ Table '${tableName}' NOT FOUND (404)`);
          results.tablesMissing.push(tableName);
        } else {
          console.log(`⚠️ Table '${tableName}' - unexpected status: ${response.status}`);
          const errorText = await response.text();
          results.errors.push(`${tableName}: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        console.log(`❌ Error checking table '${tableName}':`, error.message);
        results.errors.push(`${tableName}: ${error.message}`);
      }
    }

    // Test a simple INSERT to see RLS behavior
    console.log('🔐 Testing RLS policies with a test insert...');
    try {
      const testResponse = await fetch(`${supabaseUrl}/rest/v1/tmdb_cache`, {
        method: 'POST',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({
          cache_key: 'schema-test',
          title: 'Test Movie',
          year: 2024,
          tmdb_data: { test: true },
          cached_at: new Date().toISOString()
        }),
        timeout: 5000
      });

      if (testResponse.ok || testResponse.status === 409) {
        console.log('✅ RLS policies allow INSERT operations');
      } else {
        const errorText = await testResponse.text();
        console.log(`❌ RLS policy issue: ${testResponse.status} - ${errorText}`);
        results.errors.push(`RLS test: ${testResponse.status} - ${errorText}`);
      }
    } catch (error) {
      console.log('❌ RLS test failed:', error.message);
      results.errors.push(`RLS test: ${error.message}`);
    }

  } catch (error) {
    console.error('❌ Schema test failed:', error);
    results.errors.push(`General error: ${error.message}`);
  }

  // Summary
  console.log('\n📊 DATABASE SCHEMA TEST SUMMARY:');
  console.log('==================================');
  console.log(`Tables Found: ${results.tablesFound.length}`);
  console.log(`Tables Missing: ${results.tablesMissing.length}`);
  console.log(`Errors: ${results.errors.length}`);
  
  if (results.tablesFound.length > 0) {
    console.log('\n✅ TABLES FOUND:');
    results.tablesFound.forEach(table => console.log(`  - ${table}`));
  }
  
  if (results.tablesMissing.length > 0) {
    console.log('\n❌ TABLES MISSING:');
    results.tablesMissing.forEach(table => console.log(`  - ${table}`));
  }
  
  if (results.errors.length > 0) {
    console.log('\n❌ ERRORS ENCOUNTERED:');
    results.errors.forEach((error, index) => {
      console.log(`${index + 1}. ${error}`);
    });
  }

  // Diagnosis
  if (results.tablesMissing.length > 0) {
    console.log('\n🚨 DIAGNOSIS: Missing database tables detected.');
    console.log('RECOMMENDATION: Run the database setup script to create missing tables.');
  } else if (results.errors.length > 0) {
    console.log('\n🚨 DIAGNOSIS: Tables exist but there are RLS or permission issues.');
    console.log('RECOMMENDATION: Check RLS policies and authentication.');
  } else {
    console.log('\n✅ DIAGNOSIS: Database schema appears to be complete.');
    console.log('The connectivity issues may be related to data validation or other factors.');
  }

  return results;
};
